import moment from "moment";
import { type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { useSheetStore } from "@/zustand/sheetStore";
import { Text } from "lucide-react";
import { getCardTransactionDisplayAmount } from "@/lib/utils";

const typeMap: Record<CardTransaction["type"], string> = {
  TransferIn: "Deposit",
  TransferOut: "Withdrawal",
  Fee_Consumption: "Fee",
  auth: "Authorization",
  Void: "Void",
  Topup: "Top-up",
};

export const cardColumns: ColumnDef<CardTransaction>[] = [
  {
    header: "Type",
    cell: ({ row }) => {
      return (
        <div className="break-word w-[200px] text-wrap">
          {row.original.merchantName || typeMap[row.original.type] || "-"}
        </div>
      );
    },
  },

  {
    header: "Merchant",
    cell: ({ row }) => {
      return (
        <div className="break-word w-[200px] text-wrap">
          {row.original.detail || row.original.merchantName || "-"}
        </div>
      );
    },
  },
  {
    header: "Amount",
    cell: ({ row }) => {
      const { amount, currency } = getCardTransactionDisplayAmount(
        row.original,
      );
      return (
        <div>
          {amount?.toFixed(2)} {currency}
        </div>
      );
    },
  },
  {
    header: "Date",
    cell: ({ row }) => (
      <div className="font-normal">
        {moment(row.original.transactionTime).format("DD-MM hh:mm A")}
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    header: "Action",
    cell: ({ row }) => {
      return (
        <div>
          <Button
            className="text-primary-500 bg-dark-primary-100 flex h-auto items-center gap-1 rounded-full border px-4 py-2 text-xs text-[10px] hover:bg-white"
            onClick={() =>
              useSheetStore.getState().openSheet("transaction-details", null, {
                transactionData: row.original,
                type: "card",
              })
            }
          >
            <Text className="size-2.5" />
            <span>Details</span>
          </Button>
        </div>
      );
    },
  },
];

export const StatusBadge = ({
  status,
  className,
}: {
  status: CardTransaction["status"];
  className?: string;
}) => {
  const statusConfig = {
    completed: {
      color: "text-dark-success-300",
      bgColor: "bg-dark-success-300/10",
      icon: "●",
    },
    failed: {
      color: "text-dark-error-300",
      bgColor: "bg-dark-error-300/10",
      icon: "●",
    },
    rejected: {
      color: "text-dark-error-300",
      bgColor: "bg-dark-error-300/10",
      icon: "●",
    },
    pending: {
      color: "text-dark-warning-300",
      bgColor: "bg-dark-warning-300/10",
      icon: "●",
    },
    closed: {
      color: "text-dark-warning-300",
      bgColor: "bg-dark-warning-300/10",
      icon: "●",
    },
  };

  const config =
    statusConfig[status?.toLowerCase() as keyof typeof statusConfig];

  return (
    <div
      className={`inline-flex items-center rounded-full px-4 py-2 text-[10px] ${config?.color} ${config?.bgColor} ${className}`}
    >
      <span className="mr-1">{config?.icon}</span>
      <span className="capitalize">{status}</span>
    </div>
  );
};
